<script lang="ts">
    import { range } from "@repo/utils";
    import * as pdfjs from "pdfjs-dist";

    import * as pdfjsViewer from "pdfjs-dist/web/pdf_viewer.mjs";
    import * as Card from "../ui/card/index.js";
    import { Button } from "../ui/button/index.js";
    import ZoomIn from "@lucide/svelte/icons/zoom-in";
    import ZoomOut from "@lucide/svelte/icons/zoom-out";
    import RotateCw from "@lucide/svelte/icons/rotate-cw";
    import RefreshCw from "@lucide/svelte/icons/refresh-cw";
    import type { Attachment } from "svelte/attachments";
    import { acquirePdfjsWorker } from "../../pdf-worker.js";
    import { m } from "../../messages.svelte.js";

    const { content }: { content: string } = $props();

    const worker = acquirePdfjsWorker();
    const loadingTask = $derived(
        pdfjs.getDocument({
            data: atob(content),
            cMapUrl: `${__PDFJS_CDN__}/cmaps/`,
            cMapPacked: true,
            enableXfa: true,
            worker,
        }),
    );

    const PLACEHOLDER_HEIGHT = 800;
    const ZOOM_STEP = 0.25;
    const ZOOM_MIN = 0.5;
    const ZOOM_MAX = 4;

    let zoom = $state(1);
    let rotation = $state(0);

    type PageEntry = {
        el: HTMLDivElement;
        pdfPage: pdfjs.PDFPageProxy;
        view: pdfjsViewer.PDFPageView;
    };
    const pageEntries = new Map<number, PageEntry>();

    function fitScale(entry: PageEntry) {
        const viewport = entry.pdfPage.getViewport({ scale: 1, rotation });
        return (entry.el.clientWidth / viewport.width / pdfjs.PixelsPerInch.PDF_TO_CSS_UNITS) * zoom;
    }

    async function refresh(entry: PageEntry) {
        entry.view.update({ scale: fitScale(entry), rotation });
        await entry.view.draw();
    }

    $effect(() => {
        void zoom;
        void rotation;
        for (const entry of pageEntries.values()) {
            void refresh(entry);
        }
    });

    function zoomIn() {
        zoom = Math.min(ZOOM_MAX, zoom + ZOOM_STEP);
    }

    function zoomOut() {
        zoom = Math.max(ZOOM_MIN, zoom - ZOOM_STEP);
    }

    function resetView() {
        zoom = 1;
        rotation = 0;
    }

    function rotate() {
        rotation = (rotation + 90) % 360;
    }

    function lazyPDFPage({ doc, page }: { doc: pdfjs.PDFDocumentProxy; page: number }): Attachment<HTMLDivElement> {
        return el => {
            let resizeObserver: ResizeObserver | null = null;
            let rendered = false;
            let rendering = false;
            let entry: PageEntry | null = null;

            el.style.minHeight = `${PLACEHOLDER_HEIGHT}px`;

            async function render() {
                if (rendered || rendering) return;
                rendering = true;
                try {
                    const pdfPage = await doc.getPage(page + 1);
                    const baseViewport = pdfPage.getViewport({ scale: 1, rotation });
                    const scale =
                        (el.clientWidth / baseViewport.width / pdfjs.PixelsPerInch.PDF_TO_CSS_UNITS) * zoom;
                    const view = new pdfjsViewer.PDFPageView({
                        container: el,
                        id: page + 1,
                        scale,
                        defaultViewport: pdfPage.getViewport({ scale, rotation }),
                        eventBus: new pdfjsViewer.EventBus(),
                    });
                    view.setPdfPage(pdfPage);
                    await view.draw();
                    rendered = true;
                    el.style.minHeight = "";

                    entry = { el, pdfPage, view };
                    pageEntries.set(page, entry);

                    resizeObserver = new ResizeObserver(async () => {
                        if (entry) {
                            await refresh(entry);
                        }
                    });
                    resizeObserver.observe(el);
                } finally {
                    rendering = false;
                }
            }

            const intersectionObserver = new IntersectionObserver(
                entries => {
                    for (const e of entries) {
                        if (e.isIntersecting) {
                            render();
                            intersectionObserver.disconnect();
                            break;
                        }
                    }
                },
                { rootMargin: "200px" },
            );
            intersectionObserver.observe(el);

            return () => {
                intersectionObserver.disconnect();
                resizeObserver?.disconnect();
                pageEntries.delete(page);
            };
        };
    }
</script>

{#await loadingTask.promise then doc}
    <div class="flex flex-col gap-2">
        <div class="bg-background sticky top-0 z-10 flex items-center justify-end gap-1 py-1">
            <Button
                variant="outline"
                size="icon-sm"
                onclick={zoomOut}
                disabled={zoom <= ZOOM_MIN}
                aria-label={m["pdfViewer.zoomOut"]()}
                title={m["pdfViewer.zoomOut"]()}
            >
                <ZoomOut class="size-4" />
            </Button>
            <Button
                variant="outline"
                size="icon-sm"
                onclick={resetView}
                disabled={zoom === 1 && rotation === 0}
                aria-label={m["pdfViewer.resetZoom"]()}
                title={m["pdfViewer.resetZoom"]()}
            >
                <RefreshCw class="size-4" />
            </Button>
            <Button
                variant="outline"
                size="icon-sm"
                onclick={zoomIn}
                disabled={zoom >= ZOOM_MAX}
                aria-label={m["pdfViewer.zoomIn"]()}
                title={m["pdfViewer.zoomIn"]()}
            >
                <ZoomIn class="size-4" />
            </Button>
            <Button
                variant="outline"
                size="icon-sm"
                onclick={rotate}
                aria-label={m["pdfViewer.rotate"]()}
                title={m["pdfViewer.rotate"]()}
            >
                <RotateCw class="size-4" />
            </Button>
        </div>
        {#each range(doc.numPages) as i (i)}
            <Card.Root>
                <Card.Header>
                    <Card.Description>Page {i + 1}</Card.Description>
                </Card.Header>
                <Card.Content class="overflow-x-auto">
                    <div {@attach lazyPDFPage({ doc, page: i })} class="pdfViewer singlePageView w-full"></div>
                </Card.Content>
            </Card.Root>
        {/each}
    </div>
{/await}
